import { ReactNode, useEffect, useRef, useState } from 'react';

import { useDeferSiteActivation } from '../../../hooks/use-defer-site-activation';
import { ResolveTenant } from '../../resolve-tenant';

import { buildHandoffSearchParams } from './build-handoff-search-params';
import { useDeferSiteNamingHandoff } from './defer-site-naming-handoff-context';

/**
 * Render-time gate for the Defer Site Naming Jira experiment
 * (epic D1D6-47 / ATLAS-126008). Sits at the top of `JiraProvisioningUI` so
 * it owns the decision of whether the user sees the deferred loading view
 * or the normal Jira provisioning flow.
 *
 * Behaviour:
 *
 * - `status === "idle"` (i.e. `?deferSiteNaming=true` is NOT set, which is
 *   100% of current prod traffic): render `children` unchanged.
 * - `status === "loading"`: render the `<ResolveTenant />` loading animation
 *   (rocket + progress bar) while the deferred activation hook runs. It is
 *   rendered above `BaseProvisioningUI`, so it is outside any
 *   `ScreenManagerProvider` and its `skipScreen()` is a no-op — the gate
 *   controls the transition, not the screen manager.
 * - `status === "success"`: rewrite the URL via `history.replaceState` so
 *   that `useSearchParameters` picks up the new `ondemandurl` /
 *   `provisioningRequestId` / `cloudId`, then render `children`. The
 *   D1D6-52 guard in `use-cloud-status` then releases
 *   `readyPollCloudStatus`, and the normal polling flow takes over.
 *   Per design 6.2 we use `replaceState` rather than
 *   `window.location.assign` to keep this as a client-side state
 *   transition (no full page reload, no React tree remount).
 * - `status === "error"`: render `children` and let the existing error
 *   UI path take over. NOTE: at this point the URL still has
 *   `?deferSiteNaming=true` and no `ondemandurl`, so the D1D6-52 guard
 *   in `use-cloud-status` keeps polling suppressed. The user sees
 *   whatever fallback view `JiraProvisioningUI` renders for an
 *   unresolved cloud-status (typically the standard error screen via
 *   the existing UFO failure path). We intentionally do NOT fork the
 *   error UI between deferred and control paths — keeping behaviour
 *   consistent and falling back to the proven error-handling code.
 */
export interface DeferSiteNamingGateProps {
	readonly children: ReactNode;
}

export const DeferSiteNamingGate = ({ children }: DeferSiteNamingGateProps) => {
	const activation = useDeferSiteActivation();
	const { setHandoff } = useDeferSiteNamingHandoff();
	// NOTE: the Statsig exposure for this experiment is fired on the signup
	// side, in `useDeferSiteNamingContinueUrl`, for BOTH arms. That is the only
	// point where the user is still logged out and satisfies the
	// `useLoggedOutJira` cohort. Firing here (on the confirmation page, where
	// the user is already logged in) is a no-op: `useExperiment` reports
	// not-enrolled and `fireExposure()` silently warns instead of emitting.
	// Do NOT re-add a variant-side fire here.

	// URL-rewrite handoff: when the deferred activation reports success, we
	// snapshot the current URL (before any rewrite) and call replaceState
	// with the merged URL. Both the snapshot and the rewrite live in an
	// effect — NOT in useMemo — so we don't violate React's pure-render
	// contract (a useMemo body must be pure; mutating a ref or calling
	// history.replaceState are both side-effects). The `hasHandedOff`
	// state (rather than a ref) is what flips us out of the loading view
	// and through to children, eliminating the sub-frame race where
	// children would otherwise render once with the pre-rewrite URL.
	//
	// The bail-out is via `hasRunHandoffRef` (ref) rather than the
	// `hasHandedOff` state value, deliberately keeping the state value
	// out of the effect's dep array. If we instead bailed on
	// `hasHandedOff`, the effect would have to list it as a dep and
	// would then fire an extra (no-op) time after `setHasHandedOff(true)`
	// re-renders the component. The ref + state pair gives us both the
	// single-fire side-effect (replaceState) and the re-render trigger
	// (state flip → loading view drops) without the redundant invocation.
	// See AI bot feedback on PR comment 2006808855 for the original
	// observation.
	const [hasHandedOff, setHasHandedOff] = useState(false);
	const hasRunHandoffRef = useRef(false);
	useEffect(() => {
		if (hasRunHandoffRef.current) return;
		if (activation.status !== 'success') return;
		hasRunHandoffRef.current = true;
		if (!activation.result) {
			// Defense-in-depth: `result` is typed as optional on
			// `DeferSiteActivationState` even though the hook always
			// sets it alongside `status: "success"`. If a future
			// refactor or unexpected race ever produces a result-less
			// success, we MUST still drop the loading view — otherwise
			// the user is stuck on the spinner forever with no recovery
			// path (per AI bot feedback on comment 2006850098). Falling
			// through to children lets the existing polling chain
			// surface whatever real state the URL is in (or hit the
			// existing error UI if no activation params landed),
			// converting a frozen UX into a diagnosable one.
			setHasHandedOff(true);
			return;
		}
		const handoffUrl = buildHandoffSearchParams({
			recommendedName: activation.result.recommendedName,
			response: activation.result.response,
			signupId: activation.result.signupId,
			currentUrl: window.location.href,
		});
		// D1D6-128: communicate the post-activation identifiers via
		// React Context (DeferSiteNamingHandoffProvider) so that the
		// parent template re-renders and feeds the new values into
		// CloudStatusProvider. Writing them to the URL alone would not
		// be enough -- React Router's useLocation does not observe
		// history.replaceState, so useSearchParameters would otherwise
		// return stale values to consumers above the gate forever.
		const response = activation.result.response;
		const metadata = 'metadata' in response ? response.metadata : null;
		const provisioningRequestId = metadata?.provisionRequestId || '';
		setHandoff({
			onDemandUrl: activation.result.recommendedName,
			provisioningRequestId,
			signupId: activation.result.signupId,
			orchestrationId: response.orchestrationId,
			cloudId: response.cloudId,
		});
		// Mirror to URL as a refresh-recovery aid only -- if the user
		// refreshes the page after handoff, the URL still describes
		// the active provisioning request and useSearchParameters
		// recovers the same values from there.
		window.history.replaceState(null, '', handoffUrl);
		// Triggers a re-render that drops the loading view and renders
		// children.
		setHasHandedOff(true);
	}, [activation.status, activation.result, setHandoff]);

	// Keep the loading view up through the brief window between
	// status=success and the post-rewrite re-render. Otherwise the
	// children render once with the pre-rewrite URL (no ondemandurl in
	// window.location.search), which would cause downstream consumers of
	// useSearchParameters to misfire (e.g. start polling against an empty
	// site or render the wrong view) before the rewrite re-render lands.
	if (activation.status === 'loading' || (activation.status === 'success' && !hasHandedOff)) {
		// Show the ResolveTenant loading animation (rocket + progress bar)
		// while the deferred activation runs. This is the same view the normal
		// flow uses for the RESOLVE_TENANT_LOADER screen. It is rendered here
		// ABOVE `BaseProvisioningUI`, so it is NOT inside a
		// `ScreenManagerProvider` — its internal `skipScreen()` therefore
		// resolves to the context default (a no-op `console.warn`) rather than
		// advancing a screen. That is intentional: the gate, not the screen
		// manager, owns the transition. Once activation succeeds and the URL
		// is rewritten, the gate falls through to `children`, where the real
		// provisioning flow (with a live ScreenManagerProvider) takes over.
		return <ResolveTenant />;
	}

	return <>{children}</>;
};
